"use client";

import {ReactTable, ReactTableCell, ReactTableState} from "@/shared/components/ReactTable";
import {TableRow} from "@/shared/components/ui/table";
import {useEffect, useMemo, useRef, useState} from "react";
import {autoEquColumns, AutoEquTableInterface} from "@/features/types/AutoEquTableInterface";
import {createColumnHelper, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {getAutoEquTableData} from "@/features/controllers/AutoEquTableController";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {formatDetailsTableCellValue} from "../autoDetailsUtils";

export interface AutoDetailsOptionsTabProps {
    data: AutoTableInterface;
}

const equColumnHelper = createColumnHelper<AutoEquTableInterface>();

export const AutoDetailsOptionsTab = ({data}: AutoDetailsOptionsTabProps) => {
    const [equRows, setEquRows] = useState<AutoEquTableInterface[]>([]);
    const [equState, setEquState] = useState<ReactTableState>("empty");
    const equOuterContainerRef = useRef<HTMLDivElement>(null);
    const equTableContainerRef = useRef<HTMLDivElement>(null);

    const equTableColumns = useMemo(
        () =>
            autoEquColumns.map((col) =>
                equColumnHelper.accessor(col.accessorKey, {
                    header: col.header,
                    size: col.size,
                    cell: (info) => formatDetailsTableCellValue(info.getValue()),
                })
            ),
        []
    );

    const equTable = useReactTable<AutoEquTableInterface>({
        data: equRows,
        columns: equTableColumns,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
    });

    useEffect(() => {
        if (!data.f_ord_num || !data.f_ord_d) return;

        let cancelled = false;
        setEquState("loading");

        getAutoEquTableData(String(data.f_ord_num), String(data.f_ord_d))
            .then((rows) => {
                if (!cancelled) {
                    setEquRows(rows);
                    setEquState(rows.length === 0 ? "empty" : "ready");
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                console.error("Blad pobierania danych AutoEqu:", err);
                setEquRows([]);
                setEquState("error");
            });

        return () => {
            cancelled = true;
        };
    }, [data.f_ord_num, data.f_ord_d]);

    return (
        <div className="space-y-2">
            <ReactTable
                table={equTable}
                state={equState}
                stateMessages={{ emptyMessage: "Brak danych opcji dla wybranego zamówienia." }}
                heightClassName="h-[46vh]"
                outerContainerRef={(node) => {
                    equOuterContainerRef.current = node;
                }}
                tableContainerRef={(node) => {
                    equTableContainerRef.current = node;
                }}
                renderItem={(row, virtualItem) => (
                    <TableRow
                        key={row.id}
                        style={{
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                        }}
                    >
                        {row.getVisibleCells().map((cell) => (
                            <ReactTableCell key={cell.id} cell={cell}/>
                        ))}
                    </TableRow>
                )}
            />
        </div>
    );
};
